/**
 * Attributes whose absence means `false` at render time, so `false` and absent
 * describe the same form.
 *
 * `defaultVisible` is NOT here and must never be: the renderer tests
 * `defaultVisible !== false` (`DynamicFieldPanel.tsx`), i.e. absent means
 * VISIBLE. Filing `defaultVisible: false` → absent as a no-op would hide a
 * subfield turning back on — the single most visible change this dialog exists to
 * show. `formConfigDriftItems.test.ts` asserts the exclusion.
 *
 * `repeatable` left for the same reason (CZNA-716). It reads like a plain flag and
 * is not one: `FormConfigCompiler#narrowRepeatable` computes `effective =
 * catalogDefault AND (templateGiven ? templateValue : true)` at BOTH the field and
 * the subfield level, so an absent `repeatable` means "inherit from the ontology",
 * not `false`. Wherever the ontology says repeatable — which is most slots —
 * `false` next to absent is a genuine narrowing, and filing it as noise hid it.
 *
 * The cost of leaving it out is the mirror case (a slot the ontology already calls
 * non-repeatable, where `false` and absent really do agree), which shows as one
 * noise row instead of none. Measured across all 11 packaged templates at the time
 * of the change: ONE node carries an explicit `repeatable: false`
 * (`conspect-group.json`, field 590 "Nadřazená předmětová kategorie", ontology
 * `repeatable_default: true`) and it is the real-narrowing case, while the no-op
 * case occurs ZERO times. So today the exclusion adds no noise at all and un-hides
 * one live narrowing — plus every narrowing the subfield control now lets an admin
 * make.
 *
 * Telling the two apart properly would mean consulting the MARC catalog here, which
 * this module does not have. If that ever becomes worth it, catalog-aware
 * classification is the shape — not putting the attribute back.
 */
var FALSY_DEFAULT_ATTRIBUTES = [
    'required',
    'hidden',
    'readOnly',
    'hideOnCreate',
    'formOnly',
    'hasComponents',
    'multiValue',
    'allowFreeText',
    'literalFreeText',
    'pickerPreload',
    'actionButton',
    'isHeadingTypeSwitch',
];
/**
 * Attribute name → i18n key. The visual editor's property dictionary is reused
 * wherever it already names the same thing (that dictionary is the one the ticket
 * points at); the rest are new keys in one namespace. One map, so a name cannot
 * drift between the editor and this dialog.
 *
 * An attribute missing from here renders as its raw JSON name and stays
 * substantive — see the module comment.
 */
export var DRIFT_ATTRIBUTE_MESSAGE_IDS = {
    // -- reused from the visual editor's property panel
    required: 'ui-czna-authorities.settings.forms.visual.property.required',
    repeatable: 'ui-czna-authorities.settings.forms.visual.property.repeatable',
    defaultVisible: 'ui-czna-authorities.settings.forms.visual.property.defaultVisible',
    hidden: 'ui-czna-authorities.settings.forms.visual.property.hidden',
    defaultValue: 'ui-czna-authorities.settings.forms.visual.property.defaultValue',
    colSpan: 'ui-czna-authorities.settings.forms.visual.property.colSpan',
    inputType: 'ui-czna-authorities.settings.forms.visual.property.inputType',
    placeholder: 'ui-czna-authorities.settings.forms.visual.property.placeholder',
    readOnly: 'ui-czna-authorities.settings.forms.visual.property.readOnly',
    hideOnCreate: 'ui-czna-authorities.settings.forms.visual.property.hideOnCreate',
    minInstances: 'ui-czna-authorities.settings.forms.visual.property.minInstances',
    vocabularyType: 'ui-czna-authorities.settings.forms.visual.property.vocabularyType',
    suggestVocabularyType: 'ui-czna-authorities.settings.forms.visual.property.suggestVocabularyType',
    recordType: 'ui-czna-authorities.settings.forms.visual.property.recordType',
    tag: 'ui-czna-authorities.settings.forms.visual.property.tagLabel',
    offerExcludedSubfields: 'ui-czna-authorities.settings.forms.visual.property.offerExcluded.heading',
    // -- new, drift-specific
    label: 'ui-czna-authorities.settings.forms.drift.attribute.label',
    fieldId: 'ui-czna-authorities.settings.forms.drift.attribute.fieldId',
    indicator1: 'ui-czna-authorities.settings.forms.drift.attribute.indicator1',
    indicator2: 'ui-czna-authorities.settings.forms.drift.attribute.indicator2',
    indicator1Def: 'ui-czna-authorities.settings.forms.drift.attribute.indicator1Def',
    indicator2Def: 'ui-czna-authorities.settings.forms.drift.attribute.indicator2Def',
    components: 'ui-czna-authorities.settings.forms.drift.attribute.components',
    hasComponents: 'ui-czna-authorities.settings.forms.drift.attribute.hasComponents',
    summarizeFn: 'ui-czna-authorities.settings.forms.drift.attribute.summarizeFn',
    multiValue: 'ui-czna-authorities.settings.forms.drift.attribute.multiValue',
    multiValueComponentType: 'ui-czna-authorities.settings.forms.drift.attribute.multiValueComponentType',
    formOnly: 'ui-czna-authorities.settings.forms.drift.attribute.formOnly',
    selectOptions: 'ui-czna-authorities.settings.forms.drift.attribute.selectOptions',
    visibleWhen: 'ui-czna-authorities.settings.forms.drift.attribute.visibleWhen',
    allowFreeText: 'ui-czna-authorities.settings.forms.drift.attribute.allowFreeText',
    literalFreeText: 'ui-czna-authorities.settings.forms.drift.attribute.literalFreeText',
    pickerPreload: 'ui-czna-authorities.settings.forms.drift.attribute.pickerPreload',
    alsoRecordTypes: 'ui-czna-authorities.settings.forms.drift.attribute.alsoRecordTypes',
    uriTemplate: 'ui-czna-authorities.settings.forms.drift.attribute.uriTemplate',
    actionButton: 'ui-czna-authorities.settings.forms.drift.attribute.actionButton',
    isHeadingTypeSwitch: 'ui-czna-authorities.settings.forms.drift.attribute.isHeadingTypeSwitch',
    id: 'ui-czna-authorities.settings.forms.drift.attribute.id',
    $comment: 'ui-czna-authorities.settings.forms.drift.attribute.comment',
};
var isRecord = function (value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
};
/**
 * Structural equality, insensitive to object key order.
 *
 * No longer used to produce items — the backend does that since CZNA-576 — but the
 * test fixtures build backend-shaped items with it, so it stays exported here
 * rather than being copied into them.
 */
export var deepEqual = function (a, b) {
    if (a === b)
        return true;
    if (Array.isArray(a) || Array.isArray(b)) {
        if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length)
            return false;
        return a.every(function (item, i) { return deepEqual(item, b[i]); });
    }
    if (isRecord(a) && isRecord(b)) {
        var aKeys = Object.keys(a);
        var bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length)
            return false;
        return aKeys.every(function (k) { return Object.prototype.hasOwnProperty.call(b, k)
            && deepEqual(a[k], b[k]); });
    }
    return false;
};
/**
 * Attributes whose empty array means the same as their absence (CZNA-1527:
 * `offerExcludedSubfields: []` excludes nothing, exactly like no list). The editor
 * drops the key rather than writing `[]`, but a hand-edited JSON may not.
 */
var EMPTY_LIST_DEFAULT_ATTRIBUTES = ['offerExcludedSubfields'];
var isEmptyList = function (value) { return Array.isArray(value) && value.length === 0; };
/** Absent, `null` or an empty/blank string — i.e. "no name authored here". */
var isBlankLabel = function (value) {
    return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
};
/**
 * Classify one attribute difference. `undefined` on a side means the side omits
 * the attribute.
 */
var technicalReasonFor = function (attribute, local, packagedDefault) {
    if (attribute === '$comment') {
        return 'COMMENT_ONLY';
    }
    // Only this direction. A label the packaged template DOES author, differing
    // from the stored one, is a rename and must be decided — as must a name the
    // packaged template adds where the stored config has none.
    if (attribute === 'label' && !isBlankLabel(local) && isBlankLabel(packagedDefault)) {
        return 'LABEL_MOVED_TO_CATALOG';
    }
    if (FALSY_DEFAULT_ATTRIBUTES.includes(attribute)
        && ((local === undefined && packagedDefault === false)
            || (packagedDefault === undefined && local === false))) {
        return 'NO_OP_DEFAULT';
    }
    if (EMPTY_LIST_DEFAULT_ATTRIBUTES.includes(attribute)
        && ((local === undefined && isEmptyList(packagedDefault))
            || (packagedDefault === undefined && isEmptyList(local)))) {
        return 'NO_OP_DEFAULT';
    }
    return null;
};
/**
 * Absent and explicit `null` are the same thing here, and normalising at the
 * boundary is what keeps them so.
 *
 * The backend sends an absent value as JSON `null`, but whether Jackson emits the
 * property at all depends on inclusion config — and a slice test can disagree with
 * production about that (it has before). Every rule below treats "not set" as one
 * state, and `formatDriftValue` already renders both as "neuvedeno", so collapsing
 * them here removes a distinction nothing downstream is entitled to make.
 */
var absentAsUndefined = function (value) { return (value === null ? undefined : value); };
/**
 * What shape of difference the item describes. An `attribute` means one property;
 * otherwise it is a whole subfield or a whole tab/field, and which side is missing
 * says whether it was added or dropped.
 *
 * Both discriminators arrive here already normalised, and that normalisation is
 * load-bearing: the module serializes with `default-property-inclusion: non_null`,
 * so `attribute: null` is not sent as `null` — it is not sent at all. Testing
 * `raw.attribute !== null` on the wire shape is therefore true for EVERY
 * whole-field item, which collapses five of the six kinds into `ATTRIBUTE` and
 * renders "Celé pole je nové" as a nameless bullet followed by raw JSON — the
 * exact CZNA-530 defect this dialog exists to remove.
 */
var itemKindOf = function (subfieldCode, attribute, local, packagedDefault) {
    if (attribute !== null)
        return 'ATTRIBUTE';
    if (subfieldCode !== null) {
        return local === undefined ? 'SUBFIELD_ADDED' : 'SUBFIELD_REMOVED';
    }
    if (local === undefined)
        return 'WHOLE_ADDED';
    if (packagedDefault === undefined)
        return 'WHOLE_REMOVED';
    return 'WHOLE_CHANGED';
};
/**
 * Stable within one row and readable in a testid. Deliberately NOT the merge
 * handle: the handle is a path that repeats the tab and field on every item, which
 * makes for unreadable test ids and useless React keys.
 */
var itemIdOf = function (subfieldCode, attribute) {
    if (attribute === null) {
        return subfieldCode === null ? '_whole' : "".concat(subfieldCode, "/_whole");
    }
    return "".concat(subfieldCode !== null && subfieldCode !== void 0 ? subfieldCode : '_field', "/").concat(attribute);
};
var toDriftItem = function (raw) {
    var _a, _b;
    // `?? null` and not `=== null`: see itemKindOf. Absent and null are one state on
    // every axis of this record, which is why the whole mapping happens right here
    // and nothing downstream ever touches `raw`.
    var subfieldCode = (_a = raw.subfieldCode) !== null && _a !== void 0 ? _a : null;
    var attribute = (_b = raw.attribute) !== null && _b !== void 0 ? _b : null;
    var local = absentAsUndefined(raw.local);
    var packagedDefault = absentAsUndefined(raw.packagedDefault);
    return {
        id: itemIdOf(subfieldCode, attribute),
        path: raw.path,
        kind: itemKindOf(subfieldCode, attribute, local, packagedDefault),
        changeKind: raw.kind,
        subfieldCode: subfieldCode,
        attribute: attribute,
        local: local,
        packagedDefault: packagedDefault,
        // Only a single property can be provably inert. A whole subfield appearing or
        // disappearing always changes the form.
        technicalReason: attribute === null
            ? null
            : technicalReasonFor(attribute, local, packagedDefault),
    };
};
/** Group items by subfield, preserving the order they were produced in. */
var groupBySubfield = function (items) {
    var groups = [];
    // Keyed on the code itself, `null` included — a Map takes null as a key, so no
    // sentinel string exists that a real subfield code could ever collide with.
    var byCode = new Map();
    items.forEach(function (item) {
        var key = item.subfieldCode;
        var group = byCode.get(key);
        if (group === undefined) {
            group = { subfieldCode: item.subfieldCode, items: [] };
            byCode.set(key, group);
            groups.push(group);
        }
        group.items.push(item);
    });
    return groups;
};
/** Break one drift row into readable items, noise already classified. */
export var itemizeDriftChange = function (change) {
    var items = change.items.map(toDriftItem);
    var substantive = items.filter(function (i) { return i.technicalReason === null; });
    var technical = items.filter(function (i) { return i.technicalReason !== null; });
    return {
        change: change,
        items: items,
        substantive: substantive,
        technical: technical,
        substantiveGroups: groupBySubfield(substantive),
        technicalGroups: groupBySubfield(technical),
    };
};
export var itemizeDriftChanges = function (changes) { return changes.map(itemizeDriftChange); };
export var summarizeDrift = function (itemized) { return ({
    changedEntries: itemized.length,
    substantiveEntries: itemized.filter(function (i) { return i.substantive.length > 0; }).length,
    technicalOnlyEntries: itemized.filter(function (i) { return i.substantive.length === 0; }).length,
    substantiveChanges: itemized.reduce(function (sum, i) { return sum + i.substantiveGroups.length; }, 0),
    technicalChanges: itemized.reduce(function (sum, i) { return sum + i.technicalGroups.length; }, 0),
}); };
